// src/features/movimentacao-financeiro/movimentacaoFinanceiroSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  register, save, fetchById,
  activateById, inactivateById,
} from './movimentacaoFinanceiroAPI';
import {
  IDLE_STATUS, LOADING_STATUS, SUCCESS_STATUS,
  FAILURE_STATUS, ALTERNATIVE_LOADING_STATUS,
} from '../status';
import {
  CREATE_MODE, CONSULT_MODE, UPDATE_MODE,
  NONE_MODE, NONE_BACKGROUND_MODE, BACKGROUND_CREATE_MODE,
} from '../mode';

const initialState = {
  status: IDLE_STATUS,
  mode: { main: NONE_MODE, background: NONE_BACKGROUND_MODE },
  error: null,
  response: { status: null, message: null, data: null },
  model: {}, // movimentação corrente
};

export const createAsync = createAsyncThunk(
  'movimentacaoFinanceiro/create',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await register(payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Erro ao criar lançamento');
    }
  }
);

export const inactivateByIdAsync = createAsyncThunk(
  'movimentacaoFinanceiro/inactivate',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await inactivateById(id);   // ← já existe na API
      return data;                                 // backend devolve o registro atualizado
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Erro ao inativar lançamento');
    }
  }
);

export const updateAsync = createAsyncThunk(
  'movimentacaoFinanceiro/update',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await save(payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Erro ao atualizar lançamento');
    }
  }
);

export const fetchByIdAsync = createAsyncThunk(
  'movimentacaoFinanceiro/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await fetchById(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Erro ao buscar lançamento');
    }
  }
);

const movimentacaoFinanceiroSlice = createSlice({
  name: 'movimentacaoFinanceiro',
  initialState,
  reducers: {
    changeToCreateMode: (s) => { s.mode.main = CREATE_MODE; },
    changeToConsultMode: (s) => { s.mode.main = CONSULT_MODE; },
    changeToUpdateMode: (s) => { s.mode.main = UPDATE_MODE; },
    resetMode: (s) => { s.mode.main = NONE_MODE; },
    changeToBackgroundCreateMode: (s) => { s.mode.background = BACKGROUND_CREATE_MODE; },
    resetBackgroundMode: (s) => { s.mode.background = NONE_BACKGROUND_MODE; },
    clearError: (s) => { s.error = null; },
    resetModel: (s) => { s.model = {}; },
  },
  extraReducers: (b) => {
    b
      /* CREATE -------------*/
      .addCase(createAsync.pending, (s) => { s.status = LOADING_STATUS; })
      .addCase(createAsync.fulfilled, (s, a) => { s.status = SUCCESS_STATUS; })
      .addCase(createAsync.rejected, (s, a) => { s.status = FAILURE_STATUS; s.error = a.payload; })
      /* UPDATE -------------*/
      .addCase(updateAsync.pending, (s) => { s.status = LOADING_STATUS; })
      .addCase(updateAsync.fulfilled, (s, a) => { s.status = SUCCESS_STATUS; s.model = a.payload; })
      .addCase(updateAsync.rejected, (s, a) => { s.status = FAILURE_STATUS; s.error = a.payload; })
      /* FETCH BY ID --------*/
      .addCase(fetchByIdAsync.pending, (s) => { s.status = ALTERNATIVE_LOADING_STATUS; })
      .addCase(fetchByIdAsync.fulfilled, (s, a) => { s.status = SUCCESS_STATUS; s.model = a.payload; })
      .addCase(fetchByIdAsync.rejected, (s, a) => { s.status = FAILURE_STATUS; s.error = a.payload; })
      .addCase(inactivateByIdAsync.pending, (s) => { s.status = LOADING_STATUS; })
      .addCase(inactivateByIdAsync.fulfilled, (s, a) => {
        s.status = SUCCESS_STATUS;
        if (s.model?.id === a.payload.id) s.model = a.payload;
      })
      .addCase(inactivateByIdAsync.rejected, (s, a) => { s.status = FAILURE_STATUS; s.error = a.payload; });
  },
});

export const {
  changeToCreateMode, changeToConsultMode, changeToUpdateMode,
  resetMode, changeToBackgroundCreateMode, resetBackgroundMode,
  clearError, resetModel,
} = movimentacaoFinanceiroSlice.actions;

export const selectMovimentacao = (s) => s.movimentacaoFinanceiro.model;
export const selectStatus = (s) => s.movimentacaoFinanceiro.status;
export const selectError = (s) => s.movimentacaoFinanceiro.error;
export const selectMode = (s) => s.movimentacaoFinanceiro.mode.main;

export default movimentacaoFinanceiroSlice.reducer;
